import type { AlertEvent, GatewayCommand, Heartbeat, HouseholdStatus, Movement, OrderStatus, OtaState, RejectReason, SessionKind, ShipmentSummary } from '@lib/api';

/** A cylinder as a live scan message names it. */
export interface ScanCylinder { id: number; serialNo: string; kind: string | null; nextTestDue: string | null }
export interface ScanAccepted { shipmentId: number; kind: SessionKind; manual: boolean; epc: string; at: string; cylinder: ScanCylinder }
export interface ScanRejected { shipmentId: number; kind: SessionKind; epc: string; at: string; reason: RejectReason; message: string; cylinder: ScanCylinder | null }

/** A gateway changed state (online/offline, a new boot), or sent a heartbeat (stored as the gateway sent it). */
export type GatewayMessage =
  | { gwId: string; site: string; state: string; stateAt: string; bootId: string | null; version: string | null; locationId: number | null; heartbeat?: never }
  | { gwId: string; site: string; heartbeat: Heartbeat; state?: never; locationId?: never };

/**
 * Payload of every SSE message type sent by GET /api/stream (the backend's stream.types.ts), plus the client-side
 * `reconnect`. Fields are camelCase; a gateway payload inside one (heartbeat, event data) keeps the MQTT contract's keys.
 */
export interface StreamPayloads {
  /** fresh reader events of one batch, with where they were read (null: the gateway is not placed) */
  events: { gw: string; site: string; locationId: number | null; events: { id: string; seq: number; type: string; ts: string; epc?: string; tid?: string; gwId: string }[] };
  /** cylinders arrived (a delivery note received) or registered */
  movement: Omit<Movement, 'shipmentNumber'>;
  gateway: GatewayMessage;
  /** one alert opened, acknowledged or resolved */
  alert: AlertEvent;
  /** many alerts changed at once (a monitor pass): refetch lists and counts */
  /** counts for a national client; `{}` for the others: just "refetch the alerts" */
  'alerts.updated': { opened?: number; resolved?: number };
  command: GatewayCommand;
  /**
   * One gateway's update state changed (patch its row), or many did at once / a release was uploaded or deleted
   * (`refetch`: reload the summary and the page shown).
   */
  ota: { gwId: string; otaState: OtaState; version: string | null; targetVersion: string | null; refetch?: never; release?: never } | { refetch: true; release?: string; gwId?: never; otaState?: never; version?: never; targetVersion?: never };
  'scan.accepted': ScanAccepted;
  'scan.rejected': ScanRejected;
  'shipment.updated': ShipmentSummary;
  'cylinder.updated': { ids: number[]; seenOnly?: boolean; registered?: boolean; serialNo?: string; locationId?: number | null; location?: string | null; at?: string };
  /** an order changed state (no personal data): its Hub, its courier */
  'order.updated': { id: number; number: string; status: OrderStatus; version: number; locationId: number; courierId: number | null; /** the status before this change (Phase 4; absent from older backends) */ previousStatus?: OrderStatus | null };
  /** a household changed (verified, rejected, suspended, a member, a move): ids and states only */
  'household.updated': { id: number; status: HouseholdStatus; version: number; villageCode: string; provinceCode: string; hubIds: number[] };
  /**
   * Not an SSE event: emitted after the stream reconnects or the tab becomes visible again. The backend has
   * no replay, so anything that happened meanwhile is only visible after a reload.
   */
  reconnect: null;
}
export type StreamType = keyof StreamPayloads;
/** `(type, data)` as a discriminated pair, so `if (t === 'movement')` narrows `data`. */
export type StreamArgs<K extends StreamType> = { [P in K]: [type: P, data: StreamPayloads[P]] }[K];

export const SSE_TYPES: Exclude<StreamType, 'reconnect'>[] = ['events', 'movement', 'gateway', 'alert', 'alerts.updated', 'command', 'ota', 'scan.accepted', 'scan.rejected', 'shipment.updated', 'cylinder.updated', 'order.updated', 'household.updated'];

/** Message types that change alert lists and counts. */
export const ALERT_TYPES_LIVE: ('alert' | 'alerts.updated')[] = ['alert', 'alerts.updated'];
